export interface PricingBreakdown {
  nights: number;
  nightlyRate: string;
  subtotal: string;
  cleaningFee: string;
  serviceFee: string;
  taxes: string;
  discount: string;
  total: string;
  securityDeposit?: string | null;
  breakdown: Array<{
    label: string;
    amount: string;
  }>;
  coupon?: {
    code: string;
    type: string;
    value: string;
  };
  couponError?: string | null;
  appliedDiscounts?: Array<{ type: 'coupon' | 'long_stay'; label: string; amount: string }>;
  couponDiscount?: string;
  /** Coupon when it applies, otherwise the automatic discount (legacy consumers). */
  appliedDiscount?: 'coupon' | 'long_stay' | null;
  /** Last-minute (or other pricing) promotion applied by the server. */
  appliedPromotion?: {
    id: string;
    type: string;
    title: string;
    nightlyRate: number;
    /** The same nights at their usual rates (shown crossed out). */
    regularNightlyRate?: string;
    regularSubtotal?: string;
  } | null;
  advancePricing?: {
    tier: number;
    percent: number;
    standardNightlyRate: string;
  } | null;
  minNights?: number;
  /** Server-decided "pay 50% now" availability for these dates. */
  paymentPlan?: {
    splitEligible: boolean;
    depositAmount: string | null;
    balanceAmount: string | null;
    balanceDueDate: string | null;
    reason?: string | null;
  };
}

export function formatCurrency(amount: string | number): string {
  // Handle null, undefined, or empty string cases
  if (amount === null || amount === undefined || amount === '') {
    return '$0.00';
  }
  
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Check for NaN or invalid numbers and return fallback
  if (isNaN(num) || !isFinite(num)) {
    return '$0.00';
  }
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

export function calculateTotalAmount(pricing: PricingBreakdown): string {
  const subtotal = isNaN(parseFloat(pricing.subtotal)) ? 0 : parseFloat(pricing.subtotal);
  const cleaning = isNaN(parseFloat(pricing.cleaningFee)) ? 0 : parseFloat(pricing.cleaningFee);
  const taxes = isNaN(parseFloat(pricing.taxes)) ? 0 : parseFloat(pricing.taxes);
  const discount = isNaN(parseFloat(pricing.discount)) ? 0 : parseFloat(pricing.discount);
  
  const total = subtotal + cleaning + taxes - discount;
  return isNaN(total) ? '0.00' : total.toFixed(2);
}

export function formatDate(date: string | Date): string {
  let d: Date;
  if (typeof date === 'string') {
    const trimmed = date.trim();
    // Date-only strings (YYYY-MM-DD) are parsed by `new Date()` as UTC midnight,
    // which renders a day earlier in negative-offset timezones (e.g. Hawaii, UTC-10).
    // Anchor them to local noon so the calendar day is always preserved.
    d = /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
      ? new Date(`${trimmed}T12:00:00`)
      : new Date(trimmed);
  } else {
    d = date;
  }
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

export function calculateTotalBeforeTaxes(pricing: PricingBreakdown): string {
  const mainTotal = isNaN(parseFloat(pricing.total)) ? 0 : parseFloat(pricing.total);
  const taxAmount = isNaN(parseFloat(pricing.taxes)) ? 0 : parseFloat(pricing.taxes);
  const totalBeforeTaxes = mainTotal - taxAmount;
  return isNaN(totalBeforeTaxes) ? '0.00' : totalBeforeTaxes.toFixed(2);
}
